import type { Listing } from '../api';

const HOME_BROWSE_KEY = 'homeBrowseState';
const HOME_LISTINGS_CACHE_KEY = 'homeListingsCache';
const CACHE_TTL_MS = 5 * 60 * 1000;

export interface HomeBrowseState {
  scrollY: number;
  listingsCount: number;
}

export interface HomeListingsCache {
  listings: Listing[];
  listingDistances: Record<number, number>;
  offset: number;
  hasMore: boolean;
  filtersKey: string;
  coordsKey: string;
  scrollY: number;
  savedAt: number;
  /** In-range leftovers not yet shown (distance filter). Required for Load more after back-nav. */
  pendingInRange?: Listing[];
  pendingDistances?: Record<number, number>;
}

let memoryCache: HomeListingsCache | null = null;

export function homeFiltersKey(filters: {
  state_id: string;
  city_id: string;
  category: string;
  search: string;
  item_type: string;
}): string {
  return [
    filters.state_id || '',
    filters.city_id || '',
    filters.category || '',
    filters.search.trim().toLowerCase(),
    filters.item_type || '',
  ].join('|');
}

export function homeCoordsKey(coords: { lat: number; lng: number } | null | undefined): string {
  if (!coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lng)) return '';
  return `${coords.lat.toFixed(4)},${coords.lng.toFixed(4)}`;
}

export function saveHomeBrowseState(scrollY: number, listingsCount: number) {
  if (typeof window === 'undefined') return;
  if (scrollY <= 0 && listingsCount <= 0) return;
  const state: HomeBrowseState = {
    scrollY: Math.max(0, Math.round(scrollY)),
    listingsCount: Math.max(0, listingsCount),
  };
  sessionStorage.setItem(HOME_BROWSE_KEY, JSON.stringify(state));
}

export function consumeHomeBrowseState(): HomeBrowseState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(HOME_BROWSE_KEY);
    sessionStorage.removeItem(HOME_BROWSE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HomeBrowseState;
    if (!parsed || typeof parsed !== 'object') return null;
    const scrollY = Number(parsed.scrollY);
    const listingsCount = Number(parsed.listingsCount);
    if (!Number.isFinite(scrollY) || scrollY < 0) return null;
    if (!Number.isFinite(listingsCount) || listingsCount < 0) return null;
    return { scrollY, listingsCount };
  } catch {
    sessionStorage.removeItem(HOME_BROWSE_KEY);
    return null;
  }
}

export function saveHomeListingsCache(cache: Omit<HomeListingsCache, 'savedAt'>) {
  if (typeof window === 'undefined') return;
  if (!cache.listings.length) return;
  const next: HomeListingsCache = { ...cache, savedAt: Date.now() };
  memoryCache = next;
  try {
    sessionStorage.setItem(HOME_LISTINGS_CACHE_KEY, JSON.stringify(next));
  } catch {
    /* quota / private mode — memory cache still works */
  }
}

export function readHomeListingsCache(
  filtersKey: string,
  coordsKey: string,
): HomeListingsCache | null {
  const fromMemory = memoryCache;
  if (fromMemory && isFreshMatchingCache(fromMemory, filtersKey, coordsKey)) {
    return fromMemory;
  }

  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(HOME_LISTINGS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HomeListingsCache;
    if (!isFreshMatchingCache(parsed, filtersKey, coordsKey)) return null;
    memoryCache = parsed;
    return parsed;
  } catch {
    return null;
  }
}

/** Soft lookup for list→detail image seed (any recent home cache, no filter match required). */
export function peekHomeCachedListing(id: number): Listing | null {
  if (!Number.isFinite(id) || id <= 0) return null;
  const find = (cache: HomeListingsCache | null | undefined) => {
    if (!cache?.listings?.length) return null;
    if (Date.now() - Number(cache.savedAt || 0) > CACHE_TTL_MS) return null;
    return cache.listings.find((l) => Number(l.id) === id) || null;
  };
  const fromMemory = find(memoryCache);
  if (fromMemory) return fromMemory;
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(HOME_LISTINGS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HomeListingsCache;
    const hit = find(parsed);
    if (hit) memoryCache = parsed;
    return hit;
  } catch {
    return null;
  }
}

function isFreshMatchingCache(
  cache: HomeListingsCache,
  filtersKey: string,
  coordsKey: string,
): boolean {
  if (!cache || !Array.isArray(cache.listings) || cache.listings.length === 0) return false;
  if (cache.filtersKey !== filtersKey) return false;
  if (cache.coordsKey !== coordsKey) return false;
  if (!Number.isFinite(cache.savedAt) || Date.now() - cache.savedAt > CACHE_TTL_MS) return false;
  return true;
}

export function clearHomeListingsCache() {
  memoryCache = null;
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(HOME_LISTINGS_CACHE_KEY);
  } catch {
    /* ignore */
  }
}
